import { FormProps, message } from 'antd';
import * as React from 'react';
import { useCallback } from 'react';
import {
  SubmitErrorHandler,
  SubmitHandler,
  useFormContext,
} from 'react-hook-form';
import {
  FormTransformOnSubmitNoop,
  IFormTransformOnSubmitFn,
} from './IFormTransformOnSubmitFn';
import { StyledForm } from './StyledForm';
import { useFormAugmentedContext } from './augmented/FormAugmentedContext';

export interface IFormProps extends Omit<FormProps, 'onInvalid'> {
  children: React.ReactNode;
  /**
   * Abilitare in caso di form annidato all'interno di un altro form.
   * Evita che il submit del form esterno venga inviato.
   */
  nested?: boolean;
  /**
   * Disabilita l'invio del form tramite il tasto invio.
   */
  disableEnterSubmit?: boolean;
  /**
   * Previene la navigazione tramite un Prompt in caso ci siano modifiche non salvate
   */
  hideVisualFeedback?: boolean;
  /**
   * Funzione di submit
   */
  onSubmit: (data: any, e?: React.BaseSyntheticEvent) => void | Promise<void>;
  /**
   * Funzione di errore quando l'utente prova a inviare il form ma non
   * è valido.
   */
  onInvalid?: SubmitErrorHandler<any>;
}

type HandleValidFn = (
  transformOnSubmit?: IFormTransformOnSubmitFn,
) => SubmitHandler<any>;

/**
 * Wrapper per il Form di antd per integrarlo con react-hook-form.
 *
 * Non possiamo usare il Form di antd direttamente perché non supporta
 * `onSubmit` e questo crea problemi con react-hook-form e la propagazione
 * degli eventi.
 * Tuttavia il Form di antd è necessario per il layout e la gestione degli
 * stili, quindi usiamo questo componente per integrare le due librerie.
 */
export function Form(props: IFormProps) {
  const {
    children,
    nested,
    disableEnterSubmit,
    onSubmit,
    onInvalid,
    ...otherProps
  } = props;

  const methods = useFormContext();
  const augmented = useFormAugmentedContext();

  const handleValid: HandleValidFn = useCallback(
    (transformOnSubmit: IFormTransformOnSubmitFn = FormTransformOnSubmitNoop) =>
      async (data, event) => {
        // Trasformiamo i dati prima di inviare il form, in caso sia stata
        // specificata una funzione di trasformazione.
        // In questo modo possiamo trasformare i dati prima di inviarli al
        // backend.
        transformOnSubmit(data);
        augmented.invalidSubject.next({ type: 'valid', data });
        await onSubmit(data, event);
      },
    [onSubmit, augmented.invalidSubject],
  );

  const handleInvalid: SubmitErrorHandler<any> = useCallback(
    (errors, event) => {
      augmented.invalidSubject.next({ type: 'invalid', errors });
      onInvalid?.(errors, event);
    },
    [onInvalid, augmented.invalidSubject],
  );

  /**
   * Versione del submit chiamata direttamente dall'`onSubmit` del Form.
   * In questo caso non c'è una `transformOnSubmit` personalizzata
   */
  const handleSubmit = useCallback(
    async (e?: React.FormEvent<HTMLFormElement>) => {
      try {
        await methods.handleSubmit(handleValid(), handleInvalid)(e);
      } catch (error) {
        // Nei form con `ManagedSubmitButton` l'errore viene già gestito
        // internamente, quindi non è necessario gestirlo qui.
        // Lo teniamo comunque per eventuali errori non gestiti in `onSubmit={...}` del
        // FormHandler.
        message.error("Errore durante l'invio del form");
        console.error(error);
      }

      if (nested) {
        e?.preventDefault();
      }
    },
    [methods, handleValid, handleInvalid, nested],
  );

  // Imposto la funzione di submit nel context così da poterla chiamare
  // da altri componenti programmaticamente.
  augmented.submitForm.current = (
    e?: React.BaseSyntheticEvent,
    transformOnSubmit?: IFormTransformOnSubmitFn,
  ) => {
    return new Promise<any>((resolve, reject) => {
      methods.handleSubmit(
        async (data, event) => {
          try {
            await handleValid(transformOnSubmit)(data, event);
            resolve(data);
          } catch (e) {
            reject(e);
          }
        },
        (errors, event) => {
          handleInvalid(errors, event);
          reject(errors);
        },
      )(e);
    });
  };

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLFormElement>) => {
      augmented.invalidSubject.next({ type: 'blur', event: e });
    },
    [augmented.invalidSubject],
  );

  augmented.onSubmit.current = onSubmit;

  return (
    <StyledForm {...otherProps}>
      <form // NOSONAR
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleSubmit}
        onKeyDown={
          disableEnterSubmit
            ? e => {
                // In caso di `Invio` disabilitiamo l'invio del Form. Se è una
                // TextArea, è comunque valido perché l'invio crea un accapo non
                // l'invio del form.
                if (
                  e.key === 'Enter' &&
                  !(e.target instanceof HTMLTextAreaElement)
                ) {
                  e.preventDefault();
                }
              }
            : undefined
        }
        onBlur={handleBlur}
      >
        {children}
      </form>
    </StyledForm>
  );
}
